import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, inr } from "../../lib/api";
import { PageHeader, Card, Loading, StatusBadge, inputCls, labelCls, btnPrimary, btnSecondary } from "../../components/admin/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { CheckCircle2, XCircle, Clock, ArrowLeft, CheckCheck, X as XIcon } from "lucide-react";
import { PaymentNotifications } from "../../components/admin/PaymentNotifications";
import { toast } from "sonner";

const REJECT_REASONS = ["Wrong amount", "Wrong UPI", "Duplicate transaction", "Invalid screenshot", "Transaction not found", "Suspicious proof", "Other"];

export default function PaymentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState(REJECT_REASONS[0]);
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = () => api.get(`/payments/${id}`).then((r) => { setP(r.data); setRefreshKey((k) => k + 1); }).catch(() => setP(false));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const act = async (action, rsn = "") => {
    setBusy(true);
    try {
      await api.post(`/payments/${id}/verify`, { action, reason: rsn });
      toast.success(action === "verify" ? "Payment verified — confirmation WhatsApp, email & invoice are being sent" : action === "reject" ? "Payment rejected" : "Payment marked for review");
      setRejectOpen(false); load();
    }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  if (p === null) return <Loading />;
  if (p === false) return <Card className="p-10 text-center text-slate-500">Payment not found.</Card>;
  const reg = p.registration || {};

  return (
    <div>
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>
      <PageHeader title={p.payment_id} subtitle={`Registration ${p.registration_id}`}>
        <StatusBadge status={p.status} />
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <h3 className="font-head font-semibold text-slate-900 mb-4">Verification Checks</h3>
            <div className="space-y-2.5">
              {(p.checks || []).map((c, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  {c.passed ? <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" /> : <XCircle className="w-5 h-5 text-rose-500 flex-shrink-0" />}
                  <span className={c.passed ? "text-slate-700" : "text-rose-600 font-medium"}>{c.name}</span>
                </div>
              ))}
            </div>
            {p.flags?.length > 0 && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3">
                <div className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1">Flags</div>
                <ul className="text-sm text-amber-700 list-disc pl-5 space-y-0.5">{p.flags.map((fl, i) => <li key={i}>{fl}</li>)}</ul>
              </div>
            )}
          </Card>

          <Card className="p-6">
            <h3 className="font-head font-semibold text-slate-900 mb-4">Payment Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Field label="Payment ID" value={p.payment_id} mono />
              <Field label="Payment Method" value={p.payment_method || "—"} />
              <Field label="UTR / Transaction" value={p.utr} mono />
              <Field label="Paid Amount" value={p.detected_amount != null ? inr(p.detected_amount) : "—"} />
              <Field label="Expected Amount" value={inr(p.amount)} />
              <Field label="Detected UPI" value={p.detected_upi || "—"} />
              <Field label="Expected UPI" value={p.expected_upi || "—"} />
              <Field label="Payee Name" value={p.payee_name || "—"} />
              <Field label="Payment Date" value={p.txn_date || "—"} />
              <Field label="Payment Time" value={p.txn_time || "—"} />
              <Field label="Verification Status" value={p.status} />
              <Field label="Verification Reason" value={p.reject_reason || (p.flags?.length ? p.flags.join(", ") : "—")} />
              {p.duplicate_of && <Field label="Existing Payment Reference" value={p.duplicate_of} mono />}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-head font-semibold text-slate-900 mb-4">Student & Registration</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Field label="Student" value={reg.student_name} />
              <Field label="Parent" value={reg.parent_name} />
              <Field label="Mobile" value={reg.mobile} mono />
              <Field label="School" value={reg.school_name} />
              <Field label="Program" value={reg.program_name} />
              <Field label="Level / Batch" value={`${reg.level_name || "—"} / ${reg.batch_name || "—"}`} />
            </div>
          </Card>

          {(p.payment_account_name || p.payment_upi_id) && (
            <Card className="p-6" data-testid="payment-account-used">
              <h3 className="font-head font-semibold text-slate-900 mb-4">Payment Account Used</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Field label="Account Used" value={p.payment_account_name || "—"} />
                <Field label="UPI ID Used" value={p.payment_upi_id || "—"} mono />
                <Field label="QR Amount" value={p.qr_amount != null ? inr(p.qr_amount) : "—"} />
                <Field label="QR Mapping" value={p.qr_mapping_id || "—"} mono />
              </div>
              <p className="text-xs text-slate-400 mt-3">This is the account that was active at the time of this payment. Switching the active account later does not change this record.</p>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card className="p-6 space-y-3">
            <h3 className="font-head font-semibold text-slate-900">Actions</h3>
            <button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60" onClick={() => act("verify")} disabled={busy} data-testid="verify-payment-btn"><CheckCheck className="w-4 h-4" /> Verify Payment</button>
            <button className="w-full bg-slate-100 hover:bg-slate-200 text-slate-800 font-medium py-2.5 rounded-xl flex items-center justify-center gap-2" onClick={() => act("review")} disabled={busy} data-testid="review-payment-btn"><Clock className="w-4 h-4" /> Mark for Review</button>
            <button className="w-full bg-rose-50 hover:bg-rose-100 text-rose-700 font-medium py-2.5 rounded-xl flex items-center justify-center gap-2 border border-rose-200" onClick={() => setRejectOpen(true)} disabled={busy} data-testid="reject-payment-btn"><XIcon className="w-4 h-4" /> Reject Payment</button>
            {p.verified_by && <p className="text-xs text-slate-400 pt-2">Last actioned by {p.verified_by}</p>}
            {reg.payment_status !== "verified" && <p className="text-[11px] text-slate-400">Verifying completes the registration and automatically sends the WhatsApp message, confirmation email and PDF invoice.</p>}
          </Card>
          <PaymentNotifications reg={reg} refreshKey={refreshKey} />
        </div>
      </div>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject Payment</DialogTitle></DialogHeader>
          <div><label className={labelCls}>Reason</label>
            <select className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason">{REJECT_REASONS.map((r) => <option key={r}>{r}</option>)}</select>
          </div>
          <DialogFooter>
            <button className={btnSecondary} onClick={() => setRejectOpen(false)}>Cancel</button>
            <button className="bg-rose-600 hover:bg-rose-700 text-white font-semibold py-2.5 px-4 rounded-xl" onClick={() => act("reject", reason)} disabled={busy} data-testid="confirm-reject-btn">Confirm Reject</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">{label}</div>
      <div className={`text-slate-800 ${mono ? "font-mono text-sm" : ""}`}>{value || "—"}</div>
    </div>
  );
}
